import { CSSProperties, useState } from 'react';
import { apiUrl } from '@territory/client/routes';
import { bootstrapWedgeAuth, setWedgeTokens } from '@territory/client/api';
import { isNativeIOS } from './native/nativeBridge';

/**
 * 2026-05-29 · Returning-rep sign-in with their OWN PIN.
 *
 * Re-entry path for a rep who already set a private PIN (see PinSetup). They
 * enter email + PIN; the backend (POST /api/auth/wedge-access/login-pin,
 * double rate-limited) verifies the bcrypt hash and mints the session, then we
 * bootstrap the wedge bridge and land them in PREP. We do NOT clear local
 * state here (unlike a code redemption) because this is a returning rep, not a
 * fresh activation, their cached profile/XP should rehydrate.
 *
 * Rendered inside WedgeLanding's branded glass card.
 */
export function PinLogin({ onBack, initialEmail = '' }: { onBack: () => void; initialEmail?: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [focused, setFocused] = useState<'email' | 'pin' | null>(null);

  const submit = async () => {
    setErr('');
    const e = email.trim();
    const p = pin.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e) || p.length < 4) {
      setErr('Enter your email and your PIN.');
      return;
    }
    setBusy(true);
    try {
      /* iOS shell · ask for the tokens in the body and store them. The cookies
         this endpoint sets are sameSite='lax' and the shell is a different
         origin (capacitor://localhost), so they are never sent back, and the
         usual cookie→localStorage bridge below cannot recover them either.
         Without this the rep signs in and is still treated as logged out. */
      const native = (() => { try { return isNativeIOS(); } catch { return false; } })();
      const res = await fetch(apiUrl('/api/auth/wedge-access/login-pin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(native ? { email: e, pin: p, native: true } : { email: e, pin: p }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j?.message || `Could not sign you in (${res.status}).`);
      }
      if (native) {
        const d = (await res.json().catch(() => ({}))) as { accessToken?: string; refreshToken?: string };
        /* 07-27 remediation audit · a sign-in is a possible identity swap on
           a shared device: drop the previous rep's offline session cache
           BEFORE the new tokens land (setWedgeTokens no longer clears it ·
           it also runs on every routine token rotation). */
        try { localStorage.removeItem('rv_auth_user_cache'); } catch { /* noop */ }
        if (d.accessToken || d.refreshToken) setWedgeTokens(d.accessToken, d.refreshToken);
      }
      await bootstrapWedgeAuth().catch(() => {
        /* non-fatal · on web the session cookie is already set; on native the
           tokens above are the credential. */
      });
      window.location.replace('/');
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Could not sign you in. Please try again.');
      setBusy(false);
    }
  };

  const field = (key: 'email' | 'pin', extra: CSSProperties = {}): CSSProperties => ({
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: 12,
    padding: '13px 15px',
    borderRadius: 12,
    border: `1px solid ${focused === key ? 'rgba(52,211,153,0.55)' : 'rgba(52,211,153,0.18)'}`,
    background: focused === key ? 'rgba(52,211,153,0.08)' : 'rgba(52,211,153,0.05)',
    color: '#E2E8F0',
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
    ...extra,
  });

  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: 19, fontWeight: 700, textAlign: 'center', marginBottom: 6, letterSpacing: -0.3 }}>
        Sign in with your PIN
      </div>
      <div style={{ fontSize: 13, opacity: 0.65, textAlign: 'center', lineHeight: 1.5, marginBottom: 18 }}>
        Enter your email and the PIN you set. No code needed.
      </div>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onFocus={() => setFocused('email')}
        onBlur={() => setFocused(null)}
        placeholder="Work email"
        autoCapitalize="off"
        autoComplete="email"
        inputMode="email"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="next"
        style={field('email')}
      />
      <input
        type="password"
        inputMode="numeric"
        value={pin}
        onChange={(e) => setPin(e.target.value)}
        onFocus={() => setFocused('pin')}
        onBlur={() => setFocused(null)}
        placeholder="PIN"
        autoComplete="current-password"
        enterKeyHint="go"
        style={field('pin', { letterSpacing: 4, textAlign: 'center', fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace' })}
      />
      {err && (
        <div style={{ color: '#FCA5A5', fontSize: 12.5, margin: '2px 0 12px', lineHeight: 1.4, padding: '8px 12px', background: 'rgba(252,165,165,0.08)', border: '1px solid rgba(252,165,165,0.20)', borderRadius: 10 }}>
          {err}
        </div>
      )}
      <button
        className="wedge-cta-emerald"
        onClick={submit}
        disabled={busy}
        style={{ width: '100%', padding: 14, borderRadius: 14, border: 0, background: busy ? 'rgba(52,211,153,0.42)' : '#34d399', color: '#06281c', fontSize: 14.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)', boxShadow: busy ? 'none' : '0 6px 18px rgba(52,211,153,0.28)' }}
      >
        {busy ? 'Signing you in…' : 'Sign in'}
      </button>
      <button
        className="wedge-cta-link"
        onClick={onBack}
        disabled={busy}
        style={{ width: '100%', marginTop: 10, background: 'none', border: 0, color: '#94A3B8', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', padding: '6px 8px' }}
      >
        Back
      </button>
    </div>
  );
}
